"use client";

import { useEffect, useId, useRef, useState } from "react";
import { SizeConfig } from "@/lib/listing/fieldConfigs";
import {
  formatDimensionInputNumber,
  formatDimensionsFtListing,
  parseDimensionsFtText,
} from "@/lib/listing/dimensionsFtText";
import {
  abbreviatedSizeUnitLabel,
  displaySizeTextWithoutConfiguredUnit,
  finalizeSizeTextWithUnit,
} from "@/lib/listing/sizeTextWithUnit";
import { sanitizeNumericSizeDraft } from "@/lib/listing/numericSizeDraft";
import NormalSelect from "@/components/ui/NormalSelect";

interface SizeInputProps {
  value: string;
  onChange: (size: string) => void;
  config: SizeConfig;
  className?: string;
  disabled?: boolean;
  /** When set, replaces default helper copy (e.g. generic “varies” or dimensions format). */
  helperTextOverride?: string | null;
}

function DimensionsPairFields({
  value,
  onChange,
  disabled,
  className,
  helperTextOverride,
}: {
  value: string;
  onChange: (size: string) => void;
  disabled: boolean;
  className: string;
  helperTextOverride?: string | null;
}) {
  const idBase = useId().replace(/:/g, "");
  const [lengthStr, setLengthStr] = useState("");
  const [widthStr, setWidthStr] = useState("");
  const lastEmittedRef = useRef("");

  useEffect(() => {
    if (value === lastEmittedRef.current) return;
    lastEmittedRef.current = value;
    const p = parseDimensionsFtText(value);
    setLengthStr(p ? formatDimensionInputNumber(p.length) : "");
    setWidthStr(p ? formatDimensionInputNumber(p.width) : "");
  }, [value]);

  const commit = (nextLength: string, nextWidth: string) => {
    const t1 = nextLength.trim();
    const t2 = nextWidth.trim();
    if (!t1 && !t2) {
      lastEmittedRef.current = "";
      onChange("");
      return;
    }
    if (!t1 || !t2) return;
    const len = Number.parseFloat(t1);
    const wid = Number.parseFloat(t2);
    if (
      !Number.isFinite(len) ||
      !Number.isFinite(wid) ||
      len <= 0 ||
      wid <= 0
    ) {
      return;
    }
    const next = formatDimensionsFtListing(len, wid);
    lastEmittedRef.current = next;
    onChange(next);
  };

  return (
    <div className={className}>
      <div className="flex sm:flex-col gap-3">
        <div>
          <label
            htmlFor={`${idBase}-len`}
            className="block text-xs font-medium text-foreground mb-1"
          >
            Length (ft)
          </label>
          <input
            id={`${idBase}-len`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={lengthStr}
            disabled={disabled}
            onChange={(e) => {
              const v = e.target.value;
              setLengthStr(v);
              commit(v, widthStr);
            }}
            placeholder="e.g. 4.5"
            className={`w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
              disabled
                ? "bg-secondary cursor-not-allowed opacity-60"
                : "bg-surface"
            }`}
          />
        </div>
        <div>
          <label
            htmlFor={`${idBase}-wid`}
            className="block text-xs font-medium text-foreground mb-1"
          >
            Width (ft)
          </label>
          <input
            id={`${idBase}-wid`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={widthStr}
            disabled={disabled}
            onChange={(e) => {
              const v = e.target.value;
              setWidthStr(v);
              commit(lengthStr, v);
            }}
            placeholder="e.g. 6"
            className={`w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
              disabled
                ? "bg-secondary cursor-not-allowed opacity-60"
                : "bg-surface"
            }`}
          />
        </div>
      </div>
      <p className="mt-1 text-xs text-text-light">
        {helperTextOverride ??
          "Enter length and width in feet. We build text like 4.5 ft x 6 ft for your title."}
      </p>
    </div>
  );
}

export default function SizeInput({
  value,
  onChange,
  config,
  className = "",
  disabled = false,
  helperTextOverride,
}: SizeInputProps) {
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    onChange(e.target.value);
  };

  const handleTextSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let next = e.target.value;
    if (config.valueKind) {
      next = sanitizeNumericSizeDraft(next, config.valueKind);
    }
    if (config.unit === "varies") {
      onChange(next);
      return;
    }
    onChange(finalizeSizeTextWithUnit(next, config.unit));
  };

  // Select type (category-specific fixed lists)
  if (config.inputType === "select" && config.options) {
    return (
      <div className={className}>
        <NormalSelect
          value={value}
          onChange={handleChange}
          options={config.options.map((opt) => ({ value: opt, label: opt }))}
          placeholder={config.placeholder || "Select size"}
          disabled={disabled}
        />
        {helperTextOverride ? (
          <p className="mt-1 text-xs text-text-light">{helperTextOverride}</p>
        ) : null}
      </div>
    );
  }

  if (config.inputType === "dimensions") {
    return (
      <DimensionsPairFields
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={className}
        helperTextOverride={helperTextOverride}
      />
    );
  }

  // Text type (default) with unit indicator — fixed-unit categories store "200 liters"
  // in form state (for titles/DB) while the input shows the editable part only.
  const displayValue =
    config.unit !== "varies"
      ? displaySizeTextWithoutConfiguredUnit(value, config.unit)
      : value;
  const unitAbbrev =
    config.unit !== "varies" ? abbreviatedSizeUnitLabel(config.unit) : "";
  const unitSuffix = unitAbbrev ? ` (${unitAbbrev})` : "";

  return (
    <div className={className}>
      <div className="relative">
        <input
          type="text"
          {...(config.valueKind
            ? {
                inputMode:
                  config.valueKind === "integer" ? "numeric" : "decimal",
              }
            : {})}
          value={displayValue}
          onChange={handleTextSizeChange}
          disabled={disabled}
          autoComplete="off"
          placeholder={
            config.placeholder ||
            `Enter ${config.label.toLowerCase()}${unitSuffix}`
          }
          className={`w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
            disabled
              ? "bg-secondary cursor-not-allowed opacity-60"
              : "bg-surface"
          }`}
        />
        {config.unit !== "varies" && value && unitAbbrev && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-text-light">
            {unitAbbrev}
          </span>
        )}
      </div>
      {helperTextOverride ? (
        <p className="mt-1 text-xs text-text-light">{helperTextOverride}</p>
      ) : (
        config.unit === "varies" && (
          <p className="mt-1 text-xs text-text-light">
            Include units in your input (e.g., "55 inches", "200L", "5KVA")
          </p>
        )
      )}
    </div>
  );
}
